import {
  Box,
  HStack,
  IconButton,
  Flex,
  Textarea,
  Text,
  Spinner,
  Tooltip,
  Center,
  VStack,
} from "@chakra-ui/react";
import {
  FiSend,
  FiPlus,
  FiX,
  FiFile,
  FiImage,
  FiAlertCircle,
  FiUpload,
} from "react-icons/fi";
import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
  KeyboardEvent,
} from "react";
import { useDropzone } from "react-dropzone";
import {
  useLunaStore,
  useLunaHasPendingTools,
  useLunaComposerFocusRequest,
  useLunaApi,
  LunaAttachment,
} from "state/luna";
import {
  FILE_ACCEPT_ATTR,
  MAX_FILES_PER_MESSAGE,
  formatBytes,
  isImageContentType,
  mimeForFile,
  validateFile,
} from "services/luna";

interface LunaMessageInputProps {
  sessionId: string | null;
  onSendWithoutSession?: (content: string) => void;
  placeholder?: string;
}

export interface LunaMessageInputRef {
  focus: () => void;
}

// A file the user has attached, tracked through its upload lifecycle. The
// content_type is derived once (from the extension) and reused for both the
// S3 PUT and the message payload so they always match.
interface PendingAttachment {
  id: string;
  file: File;
  contentType: string;
  status: "uploading" | "ready" | "failed";
  progress: number;
  s3Key?: string;
  error?: string;
}

export const LunaMessageInput = forwardRef<
  LunaMessageInputRef,
  LunaMessageInputProps
>(
  (
    {
      sessionId,
      onSendWithoutSession,
      placeholder = "How can I help you today?",
    },
    ref,
  ) => {
    const [value, setValue] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
    const [attachmentError, setAttachmentError] = useState<string | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const sendMessage = useLunaStore((state) => state.sendMessage);
    const hasPendingTools = useLunaHasPendingTools(sessionId);
    const { getAttachmentUploadUrl, uploadFileToS3 } = useLunaApi();

    // Attachments need a session to request an upload URL against; when there
    // is no session yet, the attachment controls stay hidden.
    const attachmentsEnabled = !!sessionId;
    const hasUploading = attachments.some((a) => a.status === "uploading");
    const readyAttachments = useMemo(
      () => attachments.filter((a) => a.status === "ready"),
      [attachments],
    );

    useImperativeHandle(ref, () => ({
      focus: () => {
        textareaRef.current?.focus();
      },
    }));

    // A card answered by typing a reply asks for the composer.
    const focusRequest = useLunaComposerFocusRequest(sessionId);
    useEffect(() => {
      if (focusRequest > 0) textareaRef.current?.focus();
    }, [focusRequest]);

    const updateAttachment = useCallback(
      (id: string, patch: Partial<PendingAttachment>) => {
        setAttachments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, ...patch } : a)),
        );
      },
      [],
    );

    const removeAttachment = useCallback((id: string) => {
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    }, []);

    const startUpload = useCallback(
      async (item: PendingAttachment) => {
        if (!sessionId) return;
        try {
          const { uploadUrl, s3Key } = await getAttachmentUploadUrl(
            sessionId,
            item.file,
          );
          await uploadFileToS3(
            uploadUrl,
            item.file,
            item.contentType,
            (fraction) => {
              updateAttachment(item.id, { progress: fraction });
            },
          );
          updateAttachment(item.id, { status: "ready", progress: 1, s3Key });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Upload failed. Try again.";
          updateAttachment(item.id, { status: "failed", error: message });
        }
      },
      [sessionId, getAttachmentUploadUrl, uploadFileToS3, updateAttachment],
    );

    const handleFilesSelected = useCallback(
      (files: FileList | File[] | null) => {
        if (!files || files.length === 0 || !sessionId) return;
        setAttachmentError(null);

        const remainingSlots = MAX_FILES_PER_MESSAGE - attachments.length;
        if (remainingSlots <= 0) {
          setAttachmentError(
            `Max ${MAX_FILES_PER_MESSAGE} attachments per message`,
          );
          return;
        }

        const incoming = Array.from(files).slice(0, remainingSlots);
        if (files.length > remainingSlots) {
          setAttachmentError(
            `Max ${MAX_FILES_PER_MESSAGE} attachments per message`,
          );
        }

        const newItems: PendingAttachment[] = [];
        for (const file of incoming) {
          const validationError = validateFile(file);
          if (validationError) {
            setAttachmentError(validationError);
            continue;
          }
          newItems.push({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            file,
            contentType: mimeForFile(file),
            status: "uploading",
            progress: 0,
          });
        }

        if (newItems.length === 0) return;

        setAttachments((prev) => [...prev, ...newItems]);
        // Upload eagerly on selection — the URL has a ~120s TTL, so firing the
        // PUT immediately keeps us well inside it; the object then lives in S3
        // until the message references it.
        newItems.forEach((item) => startUpload(item));
      },
      [attachments.length, sessionId, startUpload],
    );

    const attachmentsDisabled =
      !attachmentsEnabled ||
      hasPendingTools ||
      isSending ||
      attachments.length >= MAX_FILES_PER_MESSAGE;

    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        if (attachmentsDisabled || acceptedFiles.length === 0) return;
        handleFilesSelected(acceptedFiles);
      },
      [attachmentsDisabled, handleFilesSelected],
    );

    const { getRootProps, isDragActive } = useDropzone({
      onDrop,
      noClick: true,
      noKeyboard: true,
      disabled: attachmentsDisabled,
    });

    const handleAttachClick = useCallback(() => {
      setAttachmentError(null);
      fileInputRef.current?.click();
    }, []);

    const handleSend = useCallback(async () => {
      const trimmedValue = value.trim();
      const hasAttachments = readyAttachments.length > 0;
      if ((!trimmedValue && !hasAttachments) || isSending || hasUploading)
        return;

      setIsSending(true);

      try {
        if (sessionId) {
          const attachmentPayload: LunaAttachment[] = readyAttachments.map(
            (a) => ({
              s3_key: a.s3Key as string,
              filename: a.file.name,
              content_type: a.contentType,
              size: a.file.size,
            }),
          );
          await sendMessage(
            sessionId,
            trimmedValue,
            attachmentPayload.length > 0 ? attachmentPayload : undefined,
          );
        } else if (onSendWithoutSession) {
          await onSendWithoutSession(trimmedValue);
        }

        setValue("");
        setAttachments([]);
        setAttachmentError(null);
      } catch (err) {
        console.error("Failed to send message:", err);
      } finally {
        setIsSending(false);
      }
    }, [
      value,
      sessionId,
      sendMessage,
      onSendWithoutSession,
      isSending,
      hasUploading,
      readyAttachments,
    ]);

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };

    const isDisabled = hasPendingTools || isSending;
    const canSend = !!value.trim() || readyAttachments.length > 0;
    const atFileLimit = attachments.length >= MAX_FILES_PER_MESSAGE;

    return (
      <Box
        {...(attachmentsEnabled ? getRootProps() : {})}
        position="relative"
        bg="bg.container"
        borderRadius="container.sm"
        px="xl"
        pt="sm"
        pb="xl"
        maxW="578px"
        mx="auto"
        w="100%"
        overflow="hidden"
        boxShadow="0px 0px 64px 0px rgba(0,0,0,0.05), 0px 0px 1px 0px rgba(0,0,0,0.32), 0px 0px 2px 0px rgba(0,0,0,0.08), 0px 1px 3px 0px rgba(0,0,0,0.08)"
        sx={{ outline: "none" }}
      >
        <Flex
          direction="column"
          gap="sm"
          sx={
            isDragActive
              ? {
                  "& *": {
                    pointerEvents: "none !important",
                    caretColor: "transparent",
                  },
                }
              : undefined
          }
        >
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            isDisabled={isDisabled}
            resize="none"
            rows={1}
            border="none"
            outline="none"
            boxShadow="none"
            px={0}
            _focus={{ border: "none", boxShadow: "none" }}
            _focusVisible={{ border: "none", boxShadow: "none" }}
          />

          {/* Attachment chips */}
          {attachmentsEnabled && attachments.length > 0 && (
            <Flex wrap="wrap" gap="2xs">
              {attachments.map((attachment) => (
                <AttachmentChip
                  key={attachment.id}
                  attachment={attachment}
                  onRemove={() => removeAttachment(attachment.id)}
                />
              ))}
            </Flex>
          )}

          {/* Attachment error */}
          {attachmentsEnabled && attachmentError && (
            <HStack spacing="2xs" color="error.500">
              <Box as={FiAlertCircle} size={14} />
              <Text fontSize="sm" color="error.500">
                {attachmentError}
              </Text>
            </HStack>
          )}

          <HStack justify="flex-end" spacing="xs">
            {/* Attachment button */}
            {attachmentsEnabled && (
              <>
                <Tooltip label="Add file" hasArrow openDelay={300}>
                  <IconButton
                    aria-label="Add file"
                    icon={<FiPlus size={18} />}
                    size="md"
                    variant="ghost"
                    color="text.body.tertiary"
                    borderRadius="component.md"
                    isDisabled={isDisabled || atFileLimit}
                    onClick={handleAttachClick}
                  />
                </Tooltip>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={FILE_ACCEPT_ATTR}
                  aria-label="Add file"
                  hidden
                  onChange={(e) => {
                    handleFilesSelected(e.target.files);
                    // Reset so the same file can be picked again later
                    e.target.value = "";
                  }}
                />
              </>
            )}

            <IconButton
              aria-label="Send message"
              icon={<FiSend />}
              size="md"
              isDisabled={!canSend || isDisabled || hasUploading}
              isLoading={isSending}
              onClick={handleSend}
              sx={{
                bg: "linear-gradient(107deg, #5861de 5%, #ed6f6f 101%)",
                color: "white",
                borderRadius: "component.md",
                _hover: {
                  opacity: 0.9,
                },
                _disabled: {
                  opacity: 0.5,
                  cursor: "not-allowed",
                },
              }}
            />
          </HStack>
        </Flex>

        {attachmentsEnabled && isDragActive && (
          <Center
            position="absolute"
            inset={0}
            bg="whiteAlpha.500"
            backdropFilter="blur(6px)"
            zIndex={10}
            pointerEvents="none"
            borderRadius="container.sm"
            borderWidth="1px"
            borderStyle="dashed"
            borderColor="p.500"
          >
            <VStack spacing="2xs" textAlign="center" px="md">
              <Box as={FiUpload} fontSize="xl" color="p.600" />
              <Text
                fontSize="md"
                fontWeight="semibold"
                color="text.body.primary"
                lineHeight={1.2}
              >
                Drop to attach
              </Text>
            </VStack>
          </Center>
        )}
      </Box>
    );
  },
);

LunaMessageInput.displayName = "LunaMessageInput";

interface AttachmentChipProps {
  attachment: PendingAttachment;
  onRemove: () => void;
}

const AttachmentChip = ({ attachment, onRemove }: AttachmentChipProps) => {
  const Icon = isImageContentType(attachment.contentType) ? FiImage : FiFile;
  const isFailed = attachment.status === "failed";
  const isUploading = attachment.status === "uploading";

  const statusText = isFailed
    ? attachment.error || "Upload failed"
    : isUploading
      ? `${Math.round(attachment.progress * 100)}%`
      : formatBytes(attachment.file.size);

  return (
    <HStack
      spacing="2xs"
      align="center"
      bg="bg.subtle"
      borderWidth="1px"
      borderColor={isFailed ? "border.error" : "border.default"}
      borderRadius="component.md"
      px="xs"
      py="2xs"
      maxW="240px"
      minW={0}
    >
      <Box
        as={isFailed ? FiAlertCircle : Icon}
        size={14}
        color={isFailed ? "error.500" : "text.body.tertiary"}
        flexShrink={0}
      />
      <Text
        fontSize="sm"
        lineHeight={1}
        color="text.body.primary"
        isTruncated
        flex={1}
        minW={0}
      >
        {attachment.file.name}
      </Text>
      <Text
        fontSize="xs"
        lineHeight={1}
        color={isFailed ? "error.500" : "text.body.tertiary"}
        flexShrink={0}
        whiteSpace="nowrap"
      >
        {statusText}
      </Text>
      {isUploading ? (
        <Spinner size="xs" color="text.body.tertiary" flexShrink={0} />
      ) : null}
      <IconButton
        aria-label="Remove attachment"
        icon={<FiX size={14} />}
        size="xs"
        variant="ghost"
        minW="auto"
        h="auto"
        p="2xs"
        onClick={onRemove}
        flexShrink={0}
      />
    </HStack>
  );
};
